export type DecimalMoneyInput = number | string | bigint;

const HALALA_SCALE = 2;
const ONE_HUNDRED = 100n;

interface DecimalFraction {
  coefficient: bigint;
  scale: number;
}

function powerOfTen(exponent: number): bigint {
  if (!Number.isInteger(exponent) || exponent < 0) {
    throw new Error("money_decimal_scale_invalid");
  }
  return 10n ** BigInt(exponent);
}

function parseDecimal(value: DecimalMoneyInput, field = "amount"): DecimalFraction {
  const text = typeof value === "bigint" ? value.toString() : String(value).trim();
  if (!text || /^(?:NaN|[+-]?Infinity)$/i.test(text)) {
    throw new Error(`money_${field}_invalid`);
  }
  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(text);
  if (!match) throw new Error(`money_${field}_invalid`);

  const sign = match[1] === "-" ? -1n : 1n;
  const fractionDigits = match[3] ?? "";
  const exponent = Number(match[4] ?? "0");
  if (!Number.isSafeInteger(exponent)) {
    throw new Error(`money_${field}_invalid`);
  }

  let coefficient = BigInt(`${match[2]}${fractionDigits}`) * sign;
  let scale = fractionDigits.length - exponent;
  if (scale < 0) {
    coefficient *= powerOfTen(-scale);
    scale = 0;
  }
  return { coefficient, scale };
}

function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new Error("money_divisor_invalid");
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  const magnitude = remainder < 0n ? -remainder : remainder;
  if (magnitude * 2n < denominator) return quotient;
  return quotient + (numerator < 0n ? -1n : 1n);
}

export function roundHalfUpToScaledInteger(
  value: DecimalMoneyInput,
  fractionDigits = HALALA_SCALE,
  field = "amount",
): bigint {
  const parsed = parseDecimal(value, field);
  if (!Number.isInteger(fractionDigits) || fractionDigits < 0) {
    throw new Error("money_fraction_digits_invalid");
  }
  if (parsed.scale <= fractionDigits) {
    return parsed.coefficient * powerOfTen(fractionDigits - parsed.scale);
  }
  return divideRoundHalfUp(
    parsed.coefficient,
    powerOfTen(parsed.scale - fractionDigits),
  );
}

function safeInteger(value: bigint, field: string): number {
  const amount = Number(value);
  if (!Number.isSafeInteger(amount)) {
    throw new Error(`money_${field}_outside_safe_integer_range`);
  }
  return amount;
}

export function sarToHalalas(
  value: DecimalMoneyInput,
  field = "amount",
): number {
  return safeInteger(
    roundHalfUpToScaledInteger(value, HALALA_SCALE, field),
    `${field}_halalas`,
  );
}

export function halalasToSarString(value: number | bigint): string {
  const halalas = typeof value === "bigint" ? value : BigInt(value);
  const negative = halalas < 0n;
  const magnitude = negative ? -halalas : halalas;
  const padded = magnitude.toString().padStart(3, "0");
  const formatted = `${padded.slice(0, -2)}.${padded.slice(-2)}`;
  return negative ? `-${formatted}` : formatted;
}

export function halalasToSar(value: number | bigint): number {
  return Number(halalasToSarString(value));
}

export function roundSarHalfUp(
  value: DecimalMoneyInput,
  field = "amount",
): number {
  return halalasToSar(sarToHalalas(value, field));
}

export function formatSarAmount(
  value: DecimalMoneyInput,
  field = "amount",
): string {
  return halalasToSarString(sarToHalalas(value, field));
}

export function addSar(
  values: readonly DecimalMoneyInput[],
  field = "amount",
): number {
  const total = values.reduce<bigint>(
    (sum, value, index) =>
      sum + BigInt(sarToHalalas(value, `${field}_${index + 1}`)),
    0n,
  );
  return halalasToSar(total);
}

export function subtractSar(
  left: DecimalMoneyInput,
  right: DecimalMoneyInput,
  field = "amount",
): number {
  return halalasToSar(
    BigInt(sarToHalalas(left, `${field}_left`)) -
      BigInt(sarToHalalas(right, `${field}_right`)),
  );
}

export function multiplySarByInteger(
  value: DecimalMoneyInput,
  quantity: number | bigint,
  field = "amount",
): number {
  const normalizedQuantity = typeof quantity === "bigint"
    ? quantity
    : BigInt(quantity);
  if (normalizedQuantity < 0n) throw new Error(`money_${field}_quantity_invalid`);
  return halalasToSar(
    BigInt(sarToHalalas(value, field)) * normalizedQuantity,
  );
}

export function allocateSarProportion(
  partSar: DecimalMoneyInput,
  wholeSar: DecimalMoneyInput,
  totalAllocationSar: DecimalMoneyInput,
): number {
  const partHalalas = BigInt(sarToHalalas(partSar, "allocation_part"));
  const wholeHalalas = BigInt(sarToHalalas(wholeSar, "allocation_whole"));
  const totalAllocationHalalas = BigInt(
    sarToHalalas(totalAllocationSar, "allocation_total"),
  );
  if (wholeHalalas <= 0n || partHalalas < 0n) {
    throw new Error("money_allocation_basis_invalid");
  }
  return halalasToSar(
    divideRoundHalfUp(
      partHalalas * totalAllocationHalalas,
      wholeHalalas,
    ),
  );
}

export function calculateVatBreakdown(
  netAmountSar: DecimalMoneyInput,
  vatRatePercent: DecimalMoneyInput,
): {
  netAmountSar: number;
  vatAmountSar: number;
  grossAmountSar: number;
  netAmountHalalas: number;
  vatAmountHalalas: number;
  grossAmountHalalas: number;
} {
  const netAmountHalalas = sarToHalalas(netAmountSar, "net_amount");
  const rate = parseDecimal(vatRatePercent, "vat_rate");
  if (rate.coefficient < 0n) throw new Error("money_vat_rate_invalid");

  const vatAmountHalalasBigInt = divideRoundHalfUp(
    BigInt(netAmountHalalas) * rate.coefficient,
    ONE_HUNDRED * powerOfTen(rate.scale),
  );
  const grossAmountHalalasBigInt =
    BigInt(netAmountHalalas) + vatAmountHalalasBigInt;
  const vatAmountHalalas = safeInteger(
    vatAmountHalalasBigInt,
    "vat_amount_halalas",
  );
  const grossAmountHalalas = safeInteger(
    grossAmountHalalasBigInt,
    "gross_amount_halalas",
  );

  return {
    netAmountSar: halalasToSar(netAmountHalalas),
    vatAmountSar: halalasToSar(vatAmountHalalas),
    grossAmountSar: halalasToSar(grossAmountHalalas),
    netAmountHalalas,
    vatAmountHalalas,
    grossAmountHalalas,
  };
}

export function calculateNetFromGross(
  grossAmountSar: DecimalMoneyInput,
  vatRatePercent: DecimalMoneyInput,
): {
  netAmountSar: number;
  vatAmountSar: number;
  grossAmountSar: number;
  netAmountHalalas: number;
  vatAmountHalalas: number;
  grossAmountHalalas: number;
} {
  const grossAmountHalalas = sarToHalalas(
    grossAmountSar,
    "gross_amount",
  );
  const rate = parseDecimal(vatRatePercent, "vat_rate");
  if (rate.coefficient < 0n) throw new Error("money_vat_rate_invalid");
  const percentageScale = ONE_HUNDRED * powerOfTen(rate.scale);
  const netAmountHalalasBigInt = divideRoundHalfUp(
    BigInt(grossAmountHalalas) * percentageScale,
    percentageScale + rate.coefficient,
  );
  const vatAmountHalalasBigInt =
    BigInt(grossAmountHalalas) - netAmountHalalasBigInt;
  const netAmountHalalas = safeInteger(
    netAmountHalalasBigInt,
    "net_amount_halalas",
  );
  const vatAmountHalalas = safeInteger(
    vatAmountHalalasBigInt,
    "vat_amount_halalas",
  );
  return {
    netAmountSar: halalasToSar(netAmountHalalas),
    vatAmountSar: halalasToSar(vatAmountHalalas),
    grossAmountSar: halalasToSar(grossAmountHalalas),
    netAmountHalalas,
    vatAmountHalalas,
    grossAmountHalalas,
  };
}

export function monetaryValuesEqual(
  left: DecimalMoneyInput,
  right: DecimalMoneyInput,
): boolean {
  return sarToHalalas(left, "left") === sarToHalalas(right, "right");
}

export function assertNetVatGrossInvariant(input: {
  netAmountSar: DecimalMoneyInput;
  vatAmountSar: DecimalMoneyInput;
  grossAmountSar: DecimalMoneyInput;
}): {
  netAmountHalalas: number;
  vatAmountHalalas: number;
  grossAmountHalalas: number;
} {
  const netAmountHalalas = sarToHalalas(input.netAmountSar, "net_amount");
  const vatAmountHalalas = sarToHalalas(input.vatAmountSar, "vat_amount");
  const grossAmountHalalas = sarToHalalas(input.grossAmountSar, "gross_amount");
  if (netAmountHalalas + vatAmountHalalas !== grossAmountHalalas) {
    throw new Error("money_net_vat_gross_mismatch");
  }
  return { netAmountHalalas, vatAmountHalalas, grossAmountHalalas };
}
